import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  ChevronRight, ChevronDown, Building2, Target, TrendingUp, TrendingDown, 
  Minus, DollarSign, Users, Settings, GraduationCap, BarChart3, 
  PieChart, Gauge, LayoutDashboard, List, Cpu, Megaphone, Landmark,
  Calculator, Server, Scale, ArrowRight, Sparkles, Globe, Plus, Trophy, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import HeaderGlow from '../components/HeaderGlow';
import { mockCompanies } from '../mock/data';
import { 
  mockSectors, mockCorporateFunctions, companySectorMap, 
  bscPerspectives, mockObjectives, calculateEntityScore, 
  calculatePerspectiveScore, getStatusColor 
} from '../mock/strategyData';
import CompanyScorecard from '../components/strategy/CompanyScorecard';
import StrategyDashboard from '../components/strategy/StrategyDashboard';
import KPIManagement from '../components/strategy/KPIManagement';
import KPIDetail from '../components/strategy/KPIDetail';
import SectionHeader from '../components/shared/SectionHeader';

// Icon map for dynamic rendering
const iconMap = {
  DollarSign, Users, Settings, GraduationCap, Cpu, Megaphone, Landmark,
  Calculator, Server, Scale, Building2, Target
};

// Score Circle Component
const ScoreCircle = ({ score, size = 'md', label, sublabel }) => {
  const sizeClasses = {
    sm: 'w-16 h-16 text-lg',
    md: 'w-24 h-24 text-2xl',
    lg: 'w-32 h-32 text-3xl',
    xl: 'w-40 h-40 text-4xl'
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'from-emerald-500 to-emerald-600';
    if (score >= 75) return 'from-blue-500 to-blue-600';
    if (score >= 60) return 'from-amber-500 to-amber-600';
    return 'from-red-500 to-red-600';
  };

  return (
    <div className="flex flex-col items-center">
      <div className={`relative ${sizeClasses[size]} rounded-full bg-gradient-to-br ${getScoreColor(score)} flex items-center justify-center shadow-lg`}>
        <div className="absolute inset-1 rounded-full bg-card flex items-center justify-center">
          <span className="font-bold text-foreground">{score}%</span>
        </div>
      </div>
      {label && <span className="mt-2 text-sm font-medium text-foreground">{label}</span>}
      {sublabel && <span className="text-xs text-muted-foreground">{sublabel}</span>}
    </div>
  );
};

// Entity Card Component
const EntityCard = ({ entity, type, score, onClick, isSelected }) => {
  const getIcon = () => {
    if (type === 'company') return Building2;
    if (type === 'sector') return iconMap[entity.icon] || Globe;
    if (type === 'function') return iconMap[entity.icon] || Settings;
    return Target;
  };

  const Icon = getIcon();

  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-lg hover:border-primary/50 ${isSelected ? 'ring-2 ring-primary border-primary' : ''}`}
      onClick={onClick}
      data-testid={`entity-card-${entity.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${entity.color || '#6366f1'}20` }}
            >
              <Icon className="w-5 h-5" style={{ color: entity.color || '#6366f1' }} />
            </div>
            <div>
              <h4 className="font-semibold text-foreground text-sm">{entity.name}</h4>
              <p className="text-xs text-muted-foreground capitalize">{type}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ScoreCircle score={score} size="sm" />
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Perspective Summary Card
const PerspectiveSummaryCard = ({ perspective, score, objectivesCount }) => {
  const Icon = iconMap[perspective.icon] || Target;
  
  return (
    <Card className="overflow-hidden">
      <div 
        className="h-1"
        style={{ backgroundColor: perspective.color }}
      />
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${perspective.color}20` }}
          >
            <Icon className="w-5 h-5" style={{ color: perspective.color }} />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-foreground text-sm">{perspective.name}</h4>
            <p className="text-xs text-muted-foreground">{objectivesCount} objectives</p>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-foreground">{score}%</span>
            <p className="text-xs text-muted-foreground">Achievement</p>
          </div>
        </div>
        <Progress 
          value={score} 
          className="h-2"
          indicatorClassName={`bg-gradient-to-r`}
          style={{ '--tw-gradient-from': perspective.color, '--tw-gradient-to': perspective.color }}
        />
      </CardContent>
    </Card>
  );
};

const StrategyExecution = ({ defaultView = 'overview' }) => {
  const { currentColorTheme } = useTheme();
  const navigate = useNavigate();
  const { kpiId } = useParams();
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [expandedSectors, setExpandedSectors] = useState(['sector-tech']);
  const [activeView, setActiveView] = useState(defaultView);
  const [activeTab, setActiveTab] = useState('sectors');

  // Update active view when defaultView prop changes (route changes)
  useEffect(() => {
    if (defaultView === 'sectors') {
      setActiveView('overview');
      setActiveTab('sectors');
    } else if (defaultView === 'companies') {
      setActiveView('overview');
      setActiveTab('companies');
    } else if (defaultView === 'functions') {
      setActiveView('overview');
      setActiveTab('functions');
    } else if (defaultView === 'kpis') {
      setActiveView('kpis');
    } else if (defaultView === 'kpi-detail') {
      setActiveView('kpi-detail');
    } else if (defaultView === 'dashboards') {
      setActiveView('dashboards');
    } else if (defaultView === 'scorecards') {
      setActiveView('scorecards');
    } else {
      setActiveView('overview');
    }
  }, [defaultView]);

  // Calculate enterprise score
  const enterpriseScore = useMemo(() => calculateEntityScore('enterprise', 'enterprise'), []);

  // Calculate sector scores
  const sectorScores = useMemo(() => {
    const scores = {};
    mockSectors.forEach(sector => {
      const sectorCompanies = Object.entries(companySectorMap)
        .filter(([, sectorId]) => sectorId === sector.id)
        .map(([companyId]) => companyId);
      
      if (sectorCompanies.length === 0) {
        scores[sector.id] = 0;
        return;
      }

      let totalScore = 0;
      sectorCompanies.forEach(companyId => {
        totalScore += calculateEntityScore(companyId, 'company');
      });
      scores[sector.id] = Math.round(totalScore / sectorCompanies.length);
    });
    return scores;
  }, []);

  // Calculate company scores
  const companyScores = useMemo(() => {
    const scores = {};
    mockCompanies.forEach(company => {
      scores[company.id] = calculateEntityScore(company.id, 'company');
    });
    return scores;
  }, []);

  // Calculate function scores
  const functionScores = useMemo(() => {
    const scores = {};
    mockCorporateFunctions.forEach(func => {
      scores[func.id] = calculateEntityScore(func.id, 'function');
    });
    return scores;
  }, []);

  // Calculate perspective scores for enterprise
  const enterprisePerspectiveScores = useMemo(() => {
    const scores = {};
    bscPerspectives.forEach(p => {
      scores[p.id] = calculatePerspectiveScore('enterprise', 'enterprise', p.id);
    });
    return scores;
  }, []);

  // Get companies by sector
  const getCompaniesBySector = (sectorId) => {
    return mockCompanies.filter(company => companySectorMap[company.id] === sectorId);
  };

  const toggleSector = (sectorId) => {
    setExpandedSectors(prev => 
      prev.includes(sectorId) 
        ? prev.filter(id => id !== sectorId)
        : [...prev, sectorId]
    );
  };

  const handleEntitySelect = (entity, type) => {
    setSelectedEntity({ ...entity, entityType: type });
    setActiveView('scorecard');
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Enterprise Score Section */}
      <SectionHeader
        title="Enterprise Performance"
        icon={Trophy}
        iconColor="text-teal-500"
        gradient="from-teal-500/10 via-cyan-500/10 to-blue-500/10"
      >
        <div className="flex flex-col md:flex-row items-center gap-6">
          <ScoreCircle score={enterpriseScore} size="xl" label="Enterprise Score" sublabel="Overall Achievement" />
          <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
            {bscPerspectives.map(perspective => (
              <PerspectiveSummaryCard 
                key={perspective.id}
                perspective={perspective}
                score={enterprisePerspectiveScores[perspective.id] || 0}
                objectivesCount={mockObjectives.filter(o => o.entityId === 'enterprise' && o.perspectiveId === perspective.id).length}
              />
            ))}
          </div>
        </div>
      </SectionHeader>

      {/* Navigation Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="sectors" data-testid="tab-sectors">
            <Globe className="w-4 h-4 mr-2" />
            Sectors
          </TabsTrigger>
          <TabsTrigger value="companies" data-testid="tab-companies">
            <Building2 className="w-4 h-4 mr-2" />
            Companies
          </TabsTrigger>
          <TabsTrigger value="functions" data-testid="tab-functions">
            <Settings className="w-4 h-4 mr-2" />
            Functions
          </TabsTrigger>
        </TabsList>

        {/* Sectors Tab */}
        <TabsContent value="sectors" className="mt-4">
          <div className="space-y-4">
            {mockSectors.map(sector => {
              const sectorCompanies = getCompaniesBySector(sector.id);
              const isExpanded = expandedSectors.includes(sector.id);
              const SectorIcon = iconMap[sector.icon] || Globe;

              return (
                <Card key={sector.id} className="overflow-hidden">
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => toggleSector(sector.id)}
                    data-testid={`sector-header-${sector.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${sector.color}20` }}
                      >
                        <SectorIcon className="w-5 h-5" style={{ color: sector.color }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">{sector.name}</h3>
                        <p className="text-xs text-muted-foreground">{sectorCompanies.length} companies</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <ScoreCircle score={sectorScores[sector.id] || 0} size="sm" />
                      <Badge variant="secondary">{sectorScores[sector.id] || 0}%</Badge>
                    </div>
                  </div>
                  
                  {isExpanded && sectorCompanies.length > 0 && (
                    <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {sectorCompanies.map(company => (
                        <EntityCard 
                          key={company.id}
                          entity={company}
                          type="company"
                          score={companyScores[company.id] || 0}
                          onClick={() => handleEntitySelect(company, 'company')}
                          isSelected={selectedEntity?.id === company.id}
                        />
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Companies Tab */}
        <TabsContent value="companies" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {mockCompanies.map(company => (
              <EntityCard 
                key={company.id}
                entity={company}
                type="company"
                score={companyScores[company.id] || 0}
                onClick={() => handleEntitySelect(company, 'company')}
                isSelected={selectedEntity?.id === company.id}
              />
            ))}
          </div>
        </TabsContent>

        {/* Functions Tab */}
        <TabsContent value="functions" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {mockCorporateFunctions.map(func => (
              <EntityCard 
                key={func.id}
                entity={func}
                type="function"
                score={functionScores[func.id] || 0}
                onClick={() => handleEntitySelect(func, 'function')}
                isSelected={selectedEntity?.id === func.id}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-purple-500 via-violet-500 to-fuchsia-500'} p-8`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Target className="w-8 h-8" />
              Strategy Execution
            </h1>
            <p className="text-white/80 mt-1">Balanced Scorecard Performance Management</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost"
              className="text-white hover:bg-white/20"
              onClick={() => {
                toast.info('Sending year-1 projects to Project Management...');
                setTimeout(() => toast.success('Projects sent to Project Management module!'), 1500);
              }}
              data-testid="send-to-pm-execution-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Send to PM
            </Button>
            <Button 
              variant={activeView === 'overview' ? 'secondary' : 'ghost'}
              className={activeView === 'overview' ? '' : 'text-white hover:bg-white/20'}
              onClick={() => { setActiveView('overview'); setSelectedEntity(null); }}
              data-testid="view-overview-btn"
            >
              <List className="w-4 h-4 mr-2" />
              Overview
            </Button>
            {selectedEntity && (
              <>
                <Button 
                  variant={activeView === 'scorecard' ? 'secondary' : 'ghost'}
                  className={activeView === 'scorecard' ? '' : 'text-white hover:bg-white/20'}
                  onClick={() => setActiveView('scorecard')}
                  data-testid="view-scorecard-btn"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Scorecard
                </Button>
                <Button 
                  variant={activeView === 'dashboard' ? 'secondary' : 'ghost'}
                  className={activeView === 'dashboard' ? '' : 'text-white hover:bg-white/20'}
                  onClick={() => setActiveView('dashboard')}
                  data-testid="view-dashboard-btn"
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </>
            )}
          </div>
        </div>
        {selectedEntity && (
          <div className="mt-4 flex items-center gap-2 text-white/80">
            <span className="cursor-pointer hover:text-white" onClick={() => { setActiveView('overview'); setSelectedEntity(null); }}>
              Enterprise
            </span>
            <ArrowRight className="w-4 h-4" />
            <span className="text-white font-medium">{selectedEntity.name}</span>
          </div>
        )}
      </div>
      <HeaderGlow />

      {/* Main Content */}
      <div className="p-6">
        {activeView === 'overview' && renderOverview()}
        {activeView === 'scorecard' && selectedEntity && (
          <CompanyScorecard entity={selectedEntity} />
        )}
        {activeView === 'dashboard' && selectedEntity && (
          <StrategyDashboard entity={selectedEntity} />
        )}
        {activeView === 'kpis' && (
          <KPIManagement onSelectKPI={(kpi) => navigate(`/strategy/kpis/${kpi.id}`)} />
        )}
        {activeView === 'kpi-detail' && kpiId && (
          <KPIDetail kpiId={kpiId} onBack={() => navigate('/strategy/kpis')} />
        )}
        {activeView === 'dashboards' && !selectedEntity && (
          <div className="text-center py-12">
            <Gauge className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium text-foreground">Select an Entity</h3>
            <p className="text-muted-foreground mt-2">Choose a company or function from the Overview to view their dashboard</p>
            <Button className="mt-4" onClick={() => navigate('/strategy')}>
              Go to Overview
            </Button>
          </div>
        )}
        {activeView === 'scorecards' && !selectedEntity && (
          <div className="text-center py-12">
            <BarChart3 className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium text-foreground">Select an Entity</h3>
            <p className="text-muted-foreground mt-2">Choose a company or function from the Overview to view their scorecard</p>
            <Button className="mt-4" onClick={() => navigate('/strategy')}>
              Go to Overview
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StrategyExecution;
